import { apiBlobRequest, apiRequest } from './http';
import type { ApiItemResponse, ApiListResponse } from '../types/api';

function queryString<T extends object>(query: T): string {
  const search = new URLSearchParams();
  Object.entries(query).forEach(([key, value]) => {
    if (value !== undefined && value !== '') {
      search.set(key, String(value));
    }
  });
  const serialized = search.toString();
  return serialized ? `?${serialized}` : '';
}

// ---------------------------------------------------------------------------
// PASSAGE_SCANNER (ADII -> NWM) + ACK_PS
// ---------------------------------------------------------------------------

export interface PassageScannerListQuery {
  page?: number;
  pageSize?: number;
  referenceDeclaration?: string;
  numeroMessage?: string;
  processingStatus?: string;
  ackStatus?: string;
}

export interface PassageScannerListItem {
  id: string;
  numeroMessage: string | null;
  identifiantPassageScanner: string | null;
  referenceDeclaration: string | null;
  numeroImmatriculation: string | null;
  resultatConformite: string | null;
  validationStatus: string;
  processingStatus: string;
  ackStatus: string;
  correlationId: string;
  createdAt: string;
  updatedAt: string;
}

export interface PassageScannerDetail extends PassageScannerListItem {
  commentaire: string | null;
  scelles: string[];
  normalizedJson: unknown;
  validationReport: unknown;
  ackXmlBody: string | null;
  ackFailureReason: string | null;
  rawXmlAvailable: boolean;
}

export async function listPassageScannerMessages(query: PassageScannerListQuery): Promise<ApiListResponse<PassageScannerListItem>> {
  return apiRequest(`/maritime/scanner/passage${queryString(query)}`);
}

export async function getPassageScannerMessage(id: string): Promise<PassageScannerDetail> {
  const response = await apiRequest<ApiItemResponse<PassageScannerDetail>>(`/maritime/scanner/passage/${id}`);
  return response.data;
}

export async function getPassageScannerRawXml(id: string): Promise<string> {
  const { blob } = await apiBlobRequest(`/maritime/scanner/passage/${id}/raw`);
  return blob.text();
}

export async function reprocessPassageScannerMessage(id: string): Promise<{ id: string; processingStatus: string }> {
  const response = await apiRequest<ApiItemResponse<{ id: string; processingStatus: string }>>(`/maritime/scanner/passage/${id}/reprocess`, { method: 'POST' });
  return response.data;
}

// ---------------------------------------------------------------------------
// RESULTAT_SCANNER (NWM -> ADII) + ACK_SCANNER
// ---------------------------------------------------------------------------

export interface ResultatScannerListQuery {
  page?: number;
  pageSize?: number;
  referenceDeclaration?: string;
  identifiantScannage?: string;
  numeroMessage?: string;
  status?: string;
}

export interface ResultatScannerRecord {
  id: string;
  numeroMessage: string | null;
  identifiantScannage: string;
  numeroAMP: string;
  typeDeclaration: string;
  referenceDeclaration: string;
  resultat: string;
  status: string;
  correlationId: string | null;
  ackJson: { accuse?: { code?: string | null; description?: string | null } } | null;
  failureReason: string | null;
  createdAt: string;
  updatedAt: string;
}

export async function listResultatScannerMessages(query: ResultatScannerListQuery): Promise<ApiListResponse<ResultatScannerRecord>> {
  return apiRequest(`/maritime/scanner/resultat${queryString(query)}`);
}

export async function getResultatScannerMessage(id: string): Promise<ResultatScannerRecord> {
  const response = await apiRequest<ApiItemResponse<ResultatScannerRecord>>(`/maritime/scanner/resultat/${id}`);
  return response.data;
}

export async function getResultatScannerRawXml(id: string): Promise<string> {
  const { blob } = await apiBlobRequest(`/maritime/scanner/resultat/${id}/raw`);
  return blob.text();
}
export async function resendResultatScannerMessage(id: string): Promise<void> { await apiRequest(`/maritime/scanner/resultat/${id}/resend`, { method: 'POST' }); }

// ---------------------------------------------------------------------------
// IMAGE_SCANNER (NWM -> ADII) + ACK_IMGSCAN
// ---------------------------------------------------------------------------

export interface ImageScannerListQuery {
  page?: number;
  pageSize?: number;
  identifiant?: string;
  numeroAMP?: string;
  numeroMessage?: string;
  status?: string;
}

export interface ImageScannerImageRecord {
  id: string;
  imageIndex: number;
  status: string;
  mimeType: string;
  byteSize: number;
  sha256: string;
}

export interface ImageScannerRecord {
  id: string;
  numeroMessage: string | null;
  identifiant: string;
  numeroAMP: string;
  status: string;
  imageCount: number;
  images: ImageScannerImageRecord[];
  correlationId: string | null;
  failureReason: string | null;
  createdAt: string;
  updatedAt: string;
}

export async function listImageScannerMessages(query: ImageScannerListQuery): Promise<ApiListResponse<ImageScannerRecord>> {
  return apiRequest(`/maritime/scanner/image${queryString(query)}`);
}

export async function getImageScannerMessage(id: string): Promise<ImageScannerRecord> {
  const response = await apiRequest<ApiItemResponse<ImageScannerRecord>>(`/maritime/scanner/image/${id}`);
  return response.data;
}

export async function getImageScannerDownloadUrl(id: string, imageId: string): Promise<string> {
  const response = await apiRequest<ApiItemResponse<{ url: string }>>(`/maritime/scanner/image/${id}/images/${imageId}/download-url`);
  return response.data.url;
}
export async function getImageScannerAttempts(id: string): Promise<unknown[]> { const response = await apiRequest<ApiItemResponse<unknown[]>>(`/maritime/scanner/image/${id}/attempts`); return response.data; }
export async function resendImageScannerMessage(id: string): Promise<void> { await apiRequest(`/maritime/scanner/image/${id}/resend`, { method: 'POST' }); }
